"use client";

// Test Drive Hot List — GET /api/admin/deals?stage=TEST_DRIVE.
// Highest-intent buyers still in play, so managers can prioritize engagement.

import { adminApi, type AdminDeal, type PageMeta } from "../lib/api";
import { personName } from "../lib/adminMap";
import { useApi } from "../lib/useApi";
import Icon from "./Icon";
import Reveal from "./Reveal";
import { Skeleton } from "./Skeleton";
import { PanelEmpty, PanelError } from "./PanelState";

// Fixed time zone keeps SSR and client markup identical (no hydration drift)
// and matches the API's UTC reporting timezone.
const timeFmt = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "2-digit",
  timeZone: "UTC",
});

function testDriveTime(deal: AdminDeal) {
  const iso = deal.checkInAt ?? deal.createdAt;
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : `${timeFmt.format(d)} UTC`;
}

export default function TestDriveList() {
  const { data, loading, error, reload } = useApi<{ items: AdminDeal[]; meta: PageMeta }>(
    (signal) => adminApi.deals({ stage: "TEST_DRIVE", limit: 20 }, signal),
    [],
    "Failed to load test drives.",
  );

  const rows = [...(data?.items ?? [])].sort((a, b) => {
    const at = new Date(a.checkInAt ?? a.createdAt).getTime();
    const bt = new Date(b.checkInAt ?? b.createdAt).getTime();
    return bt - at;
  });

  return (
    <Reveal>
      <div className="neu p-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-display text-lg text-text">Test Drive Hot List</h2>
            <p className="text-[12.5px] text-text-faint">Deals currently at the test-drive stage</p>
          </div>
          {/* The count is not known until the request resolves. */}
          {loading ? (
            <Skeleton className="h-3.5 w-14 rounded" />
          ) : (
            <span className="text-[12.5px] text-text-faint">
              <span className="font-semibold text-accent tabular-nums">{rows.length}</span> leads
            </span>
          )}
        </div>

        {loading ? (
          <div className="flex flex-col gap-2.5">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-14 rounded-2xl" />
            ))}
          </div>
        ) : error ? (
          <PanelError message={error} onRetry={reload} />
        ) : rows.length === 0 ? (
          <PanelEmpty
            title="No test drives logged"
            hint="Deals appear here once a salesperson starts a test drive."
            icon="car"
          />
        ) : (
          <ul className="flex flex-col gap-2.5">
            {rows.map((deal) => (
              <li
                key={deal.id}
                className="neu-sm flex items-center justify-between gap-4 px-3.5 py-3 rounded-2xl"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <span
                    className="chip grid place-items-center w-9 h-9 rounded-xl shrink-0"
                    style={{ color: "var(--accent)", "--tint-color": "var(--mint)" } as React.CSSProperties}
                  >
                    <Icon name="car" size={17} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[13.5px] font-semibold text-text truncate">
                      {deal.customerName || "Guest Customer"}
                    </p>
                    <p className="text-[12px] text-text-faint truncate">
                      #{deal.code} · {deal.vehicle || "Vehicle not set"} · {personName(deal.salesperson)}
                    </p>
                  </div>
                </div>

                <div className="text-right shrink-0">
                  <p className="text-[12.5px] font-medium text-text-muted">Test driving</p>
                  <p className="text-[11px] text-text-faint tabular-nums">{testDriveTime(deal)}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Reveal>
  );
}
